import { CLIPBOARD_MAX_BYTES } from '@api/remote/contracts';
import { ALL_DISPLAYS, CURSORS } from '@api/remote/desktop-protocol';
import { Check, ChevronDown, ClipboardPaste, Expand, Eye, Gauge, Keyboard, KeyRound, Loader2, Lock, LockOpen, Maximize, Minimize, Monitor, PhoneOff, Power,
  RefreshCw, Shrink, TextCursorInput } from 'lucide-react';
import { Dialog, DropdownMenu } from 'radix-ui';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { formatBytes } from '../lib/format';
import { recordPath } from '../records/paths';
import { useScope } from '../scope/scope';
import { REMOTE_CHANNEL, useRecord } from '../search/hooks';
import { cn } from '../ui/cn';
import { Logo, Tooltip, useNow } from '../ui/misc';
import { onMac, specialKeys } from './keys';
import { displayName, initialViewer, QUALITY, type Quality, RemoteViewer, type ViewerState } from './session';

const MAX_TYPED = 4000;
/** Remembered per browser: whether ⌘ reaches the device as Ctrl (on unless turned off). */
const COMMAND_AS_CTRL = 'wittco.remote.commandAsCtrl';
const storedCommandAsCtrl = () => { try { return localStorage.getItem(COMMAND_AS_CTRL) !== 'off'; } catch { return true; } };

/**
 * WITTCO Remote's viewer, in a window of its own: /w/:mspId/remote/:deviceId?mode=control|view. Opening the page asks
 * for a session; closing the window (or Disconnect) ends it, and the device's user sees it end.
 */
export function RemoteViewerPage() {
  const { deviceId } = useParams();
  const [params] = useSearchParams();
  const mode = params.get('mode') === 'view' ? 'view' : 'control';
  const { mspId } = useScope();
  const device = useRecord('device', deviceId).data;
  const canvas = useRef<HTMLCanvasElement>(null);
  const surface = useRef<HTMLDivElement>(null);
  const viewer = useRef<RemoteViewer | null>(null);
  const [state, setState] = useState<ViewerState>(initialViewer);
  const [attempt, setAttempt] = useState(0);
  const [fit, setFit] = useState(true);
  const [quality, setQuality] = useState<Quality>('balanced');
  const [fullscreen, setFullscreen] = useState(false);
  const [typing, setTyping] = useState(false);
  const [clipboard, setClipboard] = useState(false);
  const [restarting, setRestarting] = useState<{ safeMode: boolean } | null>(null);
  const mac = useMemo(() => onMac(), []);
  const [commandAsCtrl, setCommandAsCtrl] = useState(storedCommandAsCtrl);
  const now = useNow(1000);
  const navigate = useNavigate();
  const control = mode === 'control';
  const windows = device?.os === 'windows' && state.desktopBackend !== 'wayland';
  const title = device?.title ?? 'Device';
  // A Mac device keeps ⌘ as ⌘.
  const keyboard = useMemo(() => ({ mac, commandAsCtrl: mac && commandAsCtrl && device?.os !== 'macos' }), [mac, commandAsCtrl, device?.os]);
  const keyboardRef = useRef(keyboard);
  keyboardRef.current = keyboard;
  useEffect(() => { viewer.current?.setKeyboard(keyboard); }, [keyboard]);
  const toggleCommandAsCtrl = () => {
    setCommandAsCtrl(!commandAsCtrl);
    try { localStorage.setItem(COMMAND_AS_CTRL, commandAsCtrl ? 'off' : 'on'); } catch { /* storage unavailable */ }
  };

  useEffect(() => {
    if (!deviceId || !canvas.current) return;
    const session = new RemoteViewer({ mspId, deviceId, mode, canvas: canvas.current, onChange: setState });
    session.setKeyboard(keyboardRef.current);
    setState(initialViewer);
    // Started on the next tick: React's development double-mount runs this cleanup first, so only one session opens.
    const timer = setTimeout(() => { viewer.current = session; void session.start(); }, 0);
    // Closing the window doesn't unmount the page, so the session is ended as the page goes away too.
    const leave = () => session.end();
    window.addEventListener('pagehide', leave);
    return () => { clearTimeout(timer); window.removeEventListener('pagehide', leave); session.end(); if (viewer.current === session) viewer.current = null; };
  }, [mspId, deviceId, mode, attempt]);

  useEffect(() => { document.title = `${title} · WITTCO Remote`; }, [title]);
  // Tells the console's device page (in another window) that a session started or ended.
  useEffect(() => {
    if ((state.phase !== 'connected' && state.phase !== 'ended') || typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(REMOTE_CHANNEL);
    // The relay records the end a moment after the session closes; say so again once it has.
    const announce = () => channel.postMessage({ deviceId });
    announce();
    const later = setTimeout(announce, 1500);
    return () => { clearTimeout(later); setTimeout(() => channel.close(), 2000); };
  }, [state.phase, deviceId]);
  useEffect(() => { if (state.phase === 'connected') surface.current?.focus(); }, [state.phase]);
  useEffect(() => {
    const changed = () => {
      const on = !!document.fullscreenElement;
      setFullscreen(on);
      // Full screen lets the browser hand over keys it normally keeps (Escape, Alt+Tab, the Windows key) where it can.
      const keyboard = (navigator as Navigator & { keyboard?: { lock?: () => Promise<void>; unlock?: () => void } }).keyboard;
      if (on) void keyboard?.lock?.().catch(() => undefined); else keyboard?.unlock?.();
    };
    document.addEventListener('fullscreenchange', changed);
    return () => document.removeEventListener('fullscreenchange', changed);
  }, []);
  // Wheel listeners must not be passive, or the page scrolls as well.
  useEffect(() => {
    const element = canvas.current;
    if (!element) return;
    const wheel = (event: WheelEvent) => { event.preventDefault(); viewer.current?.wheelMove(event); };
    element.addEventListener('wheel', wheel, { passive: false });
    return () => element.removeEventListener('wheel', wheel);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen().catch(() => undefined);
  };
  const connected = state.phase === 'connected';
  const elapsed = state.connectedAt ? Math.max(0, Math.floor((now - state.connectedAt) / 1000)) : 0;
  const clock = `${Math.floor(elapsed / 3600)}:${String(Math.floor(elapsed / 60) % 60).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`;

  return (
    <div className="flex h-dvh flex-col bg-[#0a1620] text-[#d6e2ea]">
      <header className="flex h-11 shrink-0 items-center gap-2 border-b border-white/10 bg-[#0d1d2a] px-2 sm:px-3">
        <Logo className="size-6 shrink-0" />
        <div className="min-w-0">
          <p className="truncate font-mono text-[13px] font-semibold text-white">{title}</p>
          <p className="truncate text-[11px] text-[#8fa3b3]">{device?.organizationName ?? 'WITTCO Remote'}{device?.siteName ? ` · ${device.siteName}` : ''}</p>
        </div>
        <StatusPill state={state} clock={clock} control={control} />
        <span className="flex-1" />
        {connected && <span className="hidden text-[11.5px] tabular text-[#8fa3b3] md:inline">{formatBytes(state.rate)}/s</span>}
        <div className="flex items-center gap-0.5">
        {/* Older Wayland agents advertise one display; newer agents advertise only authorized monitors. */}
        {state.displays.length > 1 && (
            <>
              <Displays state={state} onSelect={d => viewer.current?.setDisplay(d)} />
              <span className="sm:hidden">
                <Menu label="Display" icon={<Monitor className="size-4" />} disabled={state.switching}>
                  {state.displays.map(d => <Item key={d} active={state.display === d} onSelect={() => viewer.current?.setDisplay(d)}>{displayName(d, state.monitors)}</Item>)}
                </Menu>
              </span>
            </>
          )}
          <Menu label="Picture quality" icon={<Gauge className="size-4" />}>
            {(Object.keys(QUALITY) as Quality[]).map(q => (
              <Item key={q} active={quality === q} onSelect={() => { setQuality(q); viewer.current?.setQuality(q); }}>
                <span className="block">{QUALITY[q].label}</span><span className="block text-[11.5px] text-[#8fa3b3]">{QUALITY[q].detail}</span>
              </Item>
            ))}
            <DropdownMenu.Separator className="my-1 h-px bg-white/10" />
            <Item onSelect={() => viewer.current?.refresh()}><span className="flex items-center gap-2"><RefreshCw className="size-3.5" />Redraw the screen</span></Item>
          </Menu>
          <Bar label={fit ? 'Show at actual size' : 'Fit to window'} onClick={() => setFit(f => !f)}>{fit ? <Expand className="size-4" /> : <Shrink className="size-4" />}</Bar>
          {control && (
            <>
              <Menu label="Send keys" icon={<Keyboard className="size-4" />} disabled={!connected}>
                {windows && <Item onSelect={() => viewer.current?.ctrlAltDel()}>Ctrl+Alt+Del</Item>}
                {specialKeys(windows).map(k => <Item key={k.id} onSelect={() => viewer.current?.special(k.id)}>{k.label}</Item>)}
                {mac && device?.os !== 'macos' && <>
                  <DropdownMenu.Separator className="my-1 h-px bg-white/10" />
                  <DropdownMenu.CheckboxItem checked={commandAsCtrl} onSelect={event => { event.preventDefault(); toggleCommandAsCtrl(); }}
                    className="relative cursor-pointer rounded-md py-1.5 pr-2.5 pl-7 outline-none data-[highlighted]:bg-white/10">
                    <DropdownMenu.ItemIndicator className="absolute left-2 top-2"><Check className="size-3.5" /></DropdownMenu.ItemIndicator>
                    <span className="block">⌘ Command works as Ctrl</span>
                    <span className="block text-[11.5px] text-[#8fa3b3]">⌘C, ⌘V, ⌘Z and other ⌘ shortcuts reach the device as Ctrl shortcuts. For the {windows ? 'Windows' : 'Super'} key, use the item above.</span>
                  </DropdownMenu.CheckboxItem>
                </>}
              </Menu>
              <Bar label="Type text on the device" onClick={() => setTyping(true)} disabled={!connected}><TextCursorInput className="size-4" /></Bar>
              <Bar label="Copy and paste" onClick={() => setClipboard(true)} disabled={!connected}><ClipboardPaste className="size-4" /></Bar>
              {state.desktopBackend !== 'wayland' && <Bar label={state.inputLocked ? 'Let the user use their keyboard and mouse again' : 'Block the user’s keyboard and mouse while you work'}
                onClick={() => viewer.current?.lockInput(!state.inputLocked)} disabled={!connected || state.inputLocked === null} active={!!state.inputLocked}>
                {state.inputLocked ? <Lock className="size-4" /> : <LockOpen className="size-4" />}
              </Bar>}
              <Menu label="Restart" icon={<Power className="size-4" />} disabled={!connected || !!state.restart}>
                <Item onSelect={() => setRestarting({ safeMode: false })}>Restart</Item>
                {windows && <Item onSelect={() => setRestarting({ safeMode: true })}>Restart in Safe Mode with networking</Item>}
              </Menu>
            </>
          )}
          <Bar label={fullscreen ? 'Leave full screen' : 'Full screen'} onClick={toggleFullscreen}>{fullscreen ? <Minimize className="size-4" /> : <Maximize className="size-4" />}</Bar>
          {state.phase !== 'ended' && (
            <button type="button" onClick={() => viewer.current?.end()}
              className="ml-1 inline-flex h-8 items-center gap-1.5 rounded-md bg-[#c2410c] px-2.5 text-[12.5px] font-semibold text-white hover:bg-[#d4500f]">
              <PhoneOff className="size-3.5" /><span className="hidden sm:inline">Disconnect</span>
            </button>
          )}
        </div>
      </header>
      {state.notice && state.phase !== 'ended' && (
        <p role="status" className="shrink-0 border-b border-white/10 bg-[#163047] px-4 py-1.5 text-center text-[12.5px]">{state.notice}</p>
      )}

      <div ref={surface} tabIndex={0} aria-label={`Screen of ${title}`}
        className={cn('relative min-h-0 flex-1 outline-none', fit ? 'flex items-center justify-center overflow-hidden p-1' : 'overflow-auto')}
        onKeyDown={event => { if (!connected || !control) return; event.preventDefault(); viewer.current?.key(event.nativeEvent, true); }}
        onKeyUp={event => { if (!connected || !control) return; event.preventDefault(); viewer.current?.key(event.nativeEvent, false); }}
        onBlur={() => viewer.current?.releaseKeys()}>
        <canvas ref={canvas} width={1} height={1} data-testid="remote-screen"
          className={cn('block bg-black', fit ? 'max-h-full max-w-full' : '', !state.screen && 'invisible')}
          style={{ cursor: control && connected ? CURSORS[state.cursor] ?? 'default' : 'default', touchAction: 'none' }}
          onContextMenu={event => event.preventDefault()}
          onPointerMove={event => viewer.current?.pointerMove(event)}
          onPointerDown={event => { surface.current?.focus(); event.currentTarget.setPointerCapture(event.pointerId); viewer.current?.pointerButton(event, true); }}
          onPointerUp={event => viewer.current?.pointerButton(event, false)}
          onPointerCancel={() => viewer.current?.releaseButtons()} onLostPointerCapture={() => viewer.current?.releaseButtons()} />
        {state.phase === 'approving' && state.approval && (
          <Overlay>
            <span className="flex size-12 items-center justify-center rounded-full bg-[#F07522]/15 text-[#F07522]"><KeyRound className="size-6" /></span>
            <p className="mt-4 text-[16px] font-semibold text-white">Approve taking control of {title}</p>
            <p className="mt-1.5 max-w-md text-[13.5px] text-[#b8c7d3]">Controlling a device changes it, so it needs your security key or passkey, like any other change.
              The approval is for this one session.</p>
            {state.approval.error && <p role="alert" className="mt-3 max-w-md text-[13px] text-[#fca5a5]">{state.approval.error}</p>}
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <button type="button" disabled={state.approval.busy} onClick={() => void viewer.current?.approve()}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#F07522] px-3.5 text-sm font-semibold text-white hover:brightness-105 disabled:opacity-60">
                <KeyRound className="size-4" />{state.approval.busy ? 'Waiting for your key…' : 'Approve with your key'}</button>
              {deviceId && <button type="button" onClick={() => navigate(`/w/${mspId}/remote/${deviceId}?mode=view`)}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/15 px-3.5 text-sm font-medium hover:bg-white/5"><Eye className="size-4" />View only instead</button>}
            </div>
          </Overlay>
        )}
        {state.phase !== 'ended' && state.phase !== 'approving' && state.phase !== 'reconnecting' && !state.screen && (
          <Overlay>
            <Loader2 className="size-7 animate-spin text-[#F07522]" />
            {state.consent?.state === 'waiting' ? <ConsentWait consent={state.consent} title={title} now={now} wayland={state.desktopBackend === 'wayland'} control={control} /> : <>
              <p className="mt-4 text-[15px] font-medium text-white">{state.phase === 'starting' ? 'Starting a session…' : state.phase === 'connecting' ? `Connecting to ${title}…` : 'Waiting for the screen…'}</p>
              <p className="mt-1 max-w-sm text-[13px] text-[#8fa3b3]">{state.notice ?? (control
                ? state.desktopBackend === 'wayland' ? 'GNOME shows a sharing indicator while you’re connected.' : 'The person at the computer will see a “WITTCO Connected” tab while you’re connected.'
                : 'View only: your keyboard and mouse won’t reach the device.')}</p>
            </>}
          </Overlay>
        )}
        {state.phase === 'reconnecting' && state.reconnect && (
          <Overlay>
            <Loader2 className="size-7 animate-spin text-[#F07522]" />
            {state.consent?.state === 'waiting' ? <ConsentWait consent={state.consent} title={title} now={now} wayland={state.desktopBackend === 'wayland'} control={control} />
              : <ReconnectWait reconnect={state.reconnect} title={title} now={now} />}
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <button type="button" onClick={() => viewer.current?.end()} className="inline-flex h-9 items-center rounded-lg border border-white/15 px-3.5 text-sm font-medium hover:bg-white/5">
                Stop waiting</button>
            </div>
          </Overlay>
        )}
        {state.phase === 'ended' && state.ended && (
          <Overlay dim={!!state.screen}>
            <p className="text-[16px] font-semibold text-white">{state.ended.reason === 'refused' ? 'The session didn’t start' : 'Session ended'}</p>
            <p className="mt-1.5 max-w-md text-[13.5px] text-[#b8c7d3]">{state.ended.message}</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <button type="button" onClick={() => setAttempt(a => a + 1)} className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#F07522] px-3.5 text-sm font-semibold text-white hover:brightness-105">
                <RefreshCw className="size-4" />Reconnect</button>
              {deviceId && <Link to={recordPath(mspId, 'device', deviceId)} className="inline-flex h-9 items-center rounded-lg border border-white/15 px-3.5 text-sm font-medium hover:bg-white/5">Device details</Link>}
            {deviceId && control && state.ended.reason === 'refused' && <Link to={`/w/${mspId}/remote/${deviceId}?mode=view`} className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/15 px-3.5 text-sm font-medium hover:bg-white/5"><Eye className="size-4" />View only</Link>}
            {control && state.ended.reason === 'refused' && <Link to={`/w/${mspId}/settings#keys`} className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/15 px-3.5 text-sm font-medium hover:bg-white/5"><KeyRound className="size-4" />Security keys</Link>}
              <button type="button" onClick={() => window.close()} className="inline-flex h-9 items-center rounded-lg border border-white/15 px-3.5 text-sm font-medium hover:bg-white/5">Close window</button>
            </div>
          </Overlay>
        )}
      </div>
      <TypeTextDialog open={typing} onOpenChange={setTyping} wayland={state.desktopBackend === 'wayland'} onType={text => { viewer.current?.typeText(text); surface.current?.focus(); }} />
      <ClipboardDialog open={clipboard} onOpenChange={open => { setClipboard(open); if (!open) surface.current?.focus(); }} viewer={viewer.current}
        wayland={state.desktopBackend === 'wayland'} onTypeInstead={() => { setClipboard(false); setTyping(true); }} />
      <RestartDialog title={title} restart={restarting} windows={windows} wayland={state.desktopBackend === 'wayland'} onOpenChange={open => { if (!open) setRestarting(null); }}
        onRestart={safeMode => { setRestarting(null); void viewer.current?.restart(safeMode); }} />
    </div>
  );
}

/** Waiting for a device that went offline to come back (WRMM-66), with the time left to reconnect without a key touch. */
function ReconnectWait({ reconnect, title, now }: { reconnect: NonNullable<ViewerState['reconnect']>; title: string; now: number }) {
  const left = Math.max(0, Math.ceil((reconnect.until - now) / 1000));
  const clock = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
  return <>
    <p className="mt-4 text-[15px] font-medium text-white">{reconnect.restart ? `Restarting ${title}…` : reconnect.checking ? `${title} disconnected` : `${title} went offline`}</p>
    <p className="mt-1 max-w-sm text-[13px] text-[#8fa3b3]">{reconnect.checking && !reconnect.restart ? 'Checking whether it’s restarting…'
      : `You’ll be reconnected automatically when it’s back, without your key (${clock} left).`}</p>
  </>;
}

/** Confirms restarting the device (WRMM-66): normally, or into Safe Mode with networking, which lasts until a normal restart. */
function RestartDialog({ title, restart, windows, wayland, onOpenChange, onRestart }: { title: string; restart: { safeMode: boolean } | null; windows: boolean; wayland: boolean;
  onOpenChange: (open: boolean) => void; onRestart: (safeMode: boolean) => void }) {
  const safeMode = !!restart?.safeMode;
  return (
    <Dialog.Root open={!!restart} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 animate-fade" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,480px)] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-white/10 bg-[#13283a] p-5 text-[#d6e2ea] shadow-pop animate-pop">
          <Dialog.Title className="text-[15px] font-semibold text-white">{safeMode ? `Restart ${title} in Safe Mode with networking?` : `Restart ${title}?`}</Dialog.Title>
          <Dialog.Description asChild>
            <div className="mt-2 space-y-2 text-[13px] text-[#b8c7d3]">
              <p>{windows ? 'Windows asks its programs to close, as when someone restarts it there.' : 'This restarts the device.'} Anything unsaved may be lost.</p>
              {safeMode && <p>Windows starts with only what it needs, plus networking and WITTCO Remote; the WITTCO agent doesn’t run there. It keeps
                starting in Safe Mode until you restart it normally from here.</p>}
              <p>This window reconnects by itself when the device is back, within 10 minutes, without your key.</p>
              {wayland && <p>Someone must sign in to the GNOME desktop again (and allow sharing, if this customer asks first) before you can see or control it.</p>}
            </div>
          </Dialog.Description>
          <div className="mt-4 flex justify-end gap-2">
            <Dialog.Close className="inline-flex h-9 items-center rounded-lg border border-white/15 px-3.5 text-sm font-medium hover:bg-white/5">Cancel</Dialog.Close>
            <button type="button" onClick={() => onRestart(safeMode)}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#F07522] px-3.5 text-sm font-semibold text-white hover:brightness-105">
              <Power className="size-4" />{safeMode ? 'Restart in Safe Mode' : 'Restart'}</button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Waiting on the customer's consent prompt, with the time left. */
function ConsentWait({ consent, title, now, wayland, control }: { consent: Extract<ViewerState['consent'], { state: 'waiting' }>; title: string; now: number; wayland: boolean; control: boolean }) {
  const left = Math.max(0, consent.seconds - Math.floor((now - consent.since) / 1000));
  const after = consent.letInAfter ? `If nobody answers, you’ll be connected in ${left} s.` : `If nobody answers, the session won’t start (${left} s left).`;
  return consent.unattended ? <>
    <p className="mt-4 text-[15px] font-medium text-white">Nobody is signed in at {title}, or its screen is locked</p>
    <p className="mt-1 max-w-sm text-[13px] text-[#8fa3b3]">{consent.letInAfter ? `This customer lets you in after ${consent.seconds} s when nobody answers: connecting in ${left} s.`
      : `This customer needs someone at the computer to allow sessions, so this one won’t start (${left} s left).`}</p>
  </> : <>
    <p className="mt-4 text-[15px] font-medium text-white">Asking the person at {title} to allow this session…</p>
    {wayland && <p className="mt-1 max-w-sm text-[13px] text-[#8fa3b3]">{control
        ? 'In GNOME, select one or more monitors, enable “Allow Remote Interaction”, then choose Share.'
        : 'In GNOME, select one or more monitors, then choose Share.'}</p>}
    <p className="mt-1 max-w-sm text-[13px] text-[#8fa3b3]">{after}</p>
  </>;
}

function StatusPill({ state, clock, control }: { state: ViewerState; clock: string; control: boolean }) {
  const [tone, text] = state.phase === 'connected' ? ['bg-[#16a34a]', clock] : state.phase === 'ended' ? ['bg-[#64748b]', 'Ended']
    : state.phase === 'approving' ? ['bg-[#F07522]', 'Needs your approval'] : state.phase === 'reconnecting' ? ['bg-[#F07522] animate-pulse', 'Reconnecting']
    : ['bg-[#F07522] animate-pulse', 'Connecting'];
  return (
    <span className="ml-1 hidden items-center gap-1.5 rounded-full border border-white/10 px-2 py-0.5 text-[11.5px] tabular sm:inline-flex">
      <span className={cn('size-1.5 rounded-full', tone)} />{text}
      {!control && <span className="ml-1 inline-flex items-center gap-1 text-[#8fa3b3]"><Eye className="size-3" />View only</span>}
    </span>
  );
}

function Overlay({ children, dim = true }: { children: ReactNode; dim?: boolean }) {
  return <div className={cn('absolute inset-0 flex flex-col items-center justify-center p-6 text-center', dim && 'bg-[#0a1620]/85 backdrop-blur-[2px]')}>{children}</div>;
}

/**
 * The device's displays as buttons in the toolbar (WRMM-67): All, then each monitor by number, with its size and place
 * beside the main one in the tooltip. Narrow windows get the Display menu instead.
 */
function Displays({ state, onSelect }: { state: ViewerState; onSelect: (display: number) => void }) {
  const ordered = [...state.displays.filter(d => d === ALL_DISPLAYS), ...state.displays.filter(d => d !== ALL_DISPLAYS)];
  return (
    <div role="group" aria-label="Displays" className="mr-1 hidden h-8 items-center gap-0.5 rounded-md border border-white/10 p-0.5 sm:inline-flex">
      <Monitor className="mx-1 size-3.5 text-[#8fa3b3]" aria-hidden />
      {ordered.map(d => {
        const name = displayName(d, state.monitors), active = state.display === d;
        return (
          <Tooltip key={d} content={active ? `${name} (shown)` : `Show ${name}`} side="bottom">
            <button type="button" aria-label={name} aria-pressed={active} onClick={() => onSelect(d)} disabled={state.switching}
              className={cn('inline-flex h-6 min-w-6 items-center justify-center rounded px-1.5 text-[12px] font-semibold tabular text-[#b8c7d3] hover:bg-white/10 hover:text-white disabled:opacity-50',
                active && 'bg-[#F07522]/20 text-[#F07522] hover:bg-[#F07522]/25 hover:text-[#F07522]')}>
              {d === ALL_DISPLAYS ? 'All' : d}
            </button>
          </Tooltip>
        );
      })}
      {state.switching && <Loader2 className="mx-1 size-3.5 animate-spin text-[#8fa3b3]" aria-label="Switching display" />}
    </div>
  );
}

function Bar({ label, onClick, children, disabled, active }: { label: string; onClick: () => void; children: ReactNode; disabled?: boolean; active?: boolean }) {
  return (
    <Tooltip content={label} side="bottom">
      <button type="button" aria-label={label} onClick={onClick} disabled={disabled}
        className={cn('inline-flex size-8 items-center justify-center rounded-md text-[#b8c7d3] hover:bg-white/10 hover:text-white disabled:opacity-40',
          active && 'bg-[#F07522]/20 text-[#F07522]')}>{children}</button>
    </Tooltip>
  );
}

function Menu({ label, icon, children, disabled }: { label: string; icon: ReactNode; children: ReactNode; disabled?: boolean }) {
  return (
    <DropdownMenu.Root>
      <Tooltip content={label} side="bottom">
        <DropdownMenu.Trigger aria-label={label} disabled={disabled}
          className="inline-flex h-8 items-center gap-0.5 rounded-md px-1.5 text-[#b8c7d3] hover:bg-white/10 hover:text-white disabled:opacity-40 data-[state=open]:bg-white/10">
          {icon}<ChevronDown className="size-3" />
        </DropdownMenu.Trigger>
      </Tooltip>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} className="z-50 min-w-56 rounded-lg border border-white/10 bg-[#13283a] p-1 text-[13px] text-[#d6e2ea] shadow-pop animate-pop">
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function Item({ children, onSelect, active }: { children: ReactNode; onSelect: () => void; active?: boolean }) {
  return (
    <DropdownMenu.Item onSelect={onSelect} className={cn('cursor-pointer rounded-md px-2.5 py-1.5 outline-none data-[highlighted]:bg-white/10', active && 'text-[#F07522]')}>
      {children}
    </DropdownMenu.Item>
  );
}

const utf8Bytes = (text: string) => new TextEncoder().encode(text).length;
type Transfer = { state: 'idle' } | { state: 'busy' } | { state: 'done'; message: string } | { state: 'failed'; message: string };

/**
 * Copy and paste (WRMM-63): put text on the device's clipboard, or fetch the text on it. A browser reads its own
 * clipboard only when asked to by a click, so each way is a button. GNOME Wayland desktops have no clipboard yet.
 */
function ClipboardDialog({ open, onOpenChange, viewer, wayland, onTypeInstead }: { open: boolean; onOpenChange: (open: boolean) => void;
  viewer: RemoteViewer | null; wayland: boolean; onTypeInstead: () => void }) {
  const [text, setText] = useState('');
  const [fetched, setFetched] = useState<string | null>(null);
  const [send, setSend] = useState<Transfer>({ state: 'idle' });
  const [getting, setGetting] = useState<Transfer>({ state: 'idle' });
  // Each opening of the dialog: answers to requests made in an earlier one are ignored rather than shown as this one's.
  const opening = useRef(0);
  const current = (at: number) => opening.current === at;
  useEffect(() => {
    opening.current += 1;
    if (!open) return;
    setText(''); setFetched(null); setSend({ state: 'idle' }); setGetting({ state: 'idle' });
    // Starts from this computer's clipboard when the browser allows reading it.
    const at = opening.current;
    if (!wayland) void navigator.clipboard?.readText?.().then(value => { if (value && current(at)) setText(value); }, () => undefined);
  }, [open, wayland]);
  const bytes = utf8Bytes(text), tooLong = bytes > CLIPBOARD_MAX_BYTES;
  const sendIt = () => {
    if (!viewer || !text || tooLong) return;
    const at = opening.current;
    setSend({ state: 'busy' });
    viewer.sendClipboard(text).then(() => { if (current(at)) setSend({ state: 'done', message: 'On the device’s clipboard: paste it there with Ctrl+V.' }); },
      (error: Error) => { if (current(at)) setSend({ state: 'failed', message: error.message }); });
  };
  const copyHere = (value: string) => navigator.clipboard?.writeText?.(value).then(() => true, () => false) ?? Promise.resolve(false);
  const getIt = () => {
    if (!viewer) return;
    const at = opening.current;
    setGetting({ state: 'busy' }); setFetched(null);
    viewer.fetchClipboard().then(async value => {
      if (!current(at)) return;
      setFetched(value);
      if (!value) { setGetting({ state: 'done', message: 'The device’s clipboard has no text.' }); return; }
      const copied = await copyHere(value);
      if (current(at)) setGetting({ state: 'done', message: copied ? 'Copied to this computer’s clipboard.' : 'Select it below, or use Copy, to put it on this computer’s clipboard.' });
    }, (error: Error) => { if (current(at)) setGetting({ state: 'failed', message: error.message }); });
  };
  const status = (transfer: Transfer) => transfer.state === 'done' || transfer.state === 'failed'
    ? <p role="status" className={cn('mt-2 text-[12.5px]', transfer.state === 'failed' ? 'text-[#fca5a5]' : 'text-[#86efac]')}>{transfer.message}</p> : null;
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 animate-fade" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,560px)] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-white/10 bg-[#13283a] p-5 text-[#d6e2ea] shadow-pop animate-pop">
          <Dialog.Title className="text-[15px] font-semibold text-white">Copy and paste</Dialog.Title>
          {wayland ? <>
            <Dialog.Description className="mt-1 text-[13px] text-[#8fa3b3]">Copy and paste aren’t available on GNOME Wayland desktops yet. You can still type text into the device.</Dialog.Description>
            <div className="mt-4 flex justify-end gap-2">
              <Dialog.Close className="inline-flex h-9 items-center rounded-lg border border-white/15 px-3.5 text-sm font-medium hover:bg-white/5">Close</Dialog.Close>
              <button type="button" onClick={onTypeInstead} className="inline-flex h-9 items-center rounded-lg bg-[#F07522] px-3.5 text-sm font-semibold text-white">Type text instead</button>
            </div>
          </> : <>
            <Dialog.Description className="mt-1 text-[13px] text-[#8fa3b3]">Text only, up to {CLIPBOARD_MAX_BYTES / 1024} KB each way. Each copy and paste is recorded (its size, not its text).</Dialog.Description>
            <h3 className="mt-4 text-[13px] font-semibold text-white">Paste onto the device</h3>
            <textarea value={text} onChange={event => setText(event.target.value)} rows={4} aria-label="Text to put on the device’s clipboard"
              className="mt-2 w-full resize-y rounded-lg border border-white/15 bg-[#0a1620] p-2.5 font-mono text-[13px] outline-none focus:border-[#F07522]" />
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className={cn('text-[12px] tabular', tooLong ? 'text-[#fca5a5]' : 'text-[#8fa3b3]')}>{formatBytes(bytes)} of {CLIPBOARD_MAX_BYTES / 1024} KB</span>
              <button type="button" disabled={!text || tooLong || send.state === 'busy' || !viewer} onClick={sendIt}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#F07522] px-3.5 text-sm font-semibold text-white disabled:opacity-50">
                {send.state === 'busy' && <Loader2 className="size-4 animate-spin" />}Send to the device’s clipboard</button>
            </div>
            {status(send)}
            <h3 className="mt-5 text-[13px] font-semibold text-white">Copy from the device</h3>
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="text-[12px] text-[#8fa3b3]">Copy on the device first (Ctrl+C), then get it here.</span>
              <button type="button" disabled={getting.state === 'busy' || !viewer} onClick={getIt}
                className="inline-flex h-9 shrink-0 items-center gap-2 rounded-lg border border-white/15 px-3.5 text-sm font-medium hover:bg-white/5 disabled:opacity-50">
                {getting.state === 'busy' && <Loader2 className="size-4 animate-spin" />}Get the device’s clipboard</button>
            </div>
            {fetched ? <>
              <textarea value={fetched} readOnly rows={4} aria-label="The device’s clipboard" onFocus={event => event.currentTarget.select()}
                className="mt-2 w-full resize-y rounded-lg border border-white/15 bg-[#0a1620] p-2.5 font-mono text-[13px] outline-none focus:border-[#F07522]" />
              <div className="mt-2 flex justify-end">
                <button type="button" onClick={() => { const at = opening.current; void copyHere(fetched).then(ok => { if (current(at)) setGetting({ state: ok ? 'done' : 'failed',
                  message: ok ? 'Copied to this computer’s clipboard.' : 'The browser didn’t allow copying: select the text and copy it.' }); }); }}
                  className="inline-flex h-9 items-center rounded-lg border border-white/15 px-3.5 text-sm font-medium hover:bg-white/5">Copy</button>
              </div>
            </> : null}
            {status(getting)}
            <div className="mt-4 flex justify-end">
              <Dialog.Close className="inline-flex h-9 items-center rounded-lg border border-white/15 px-3.5 text-sm font-medium hover:bg-white/5">Done</Dialog.Close>
            </div>
          </>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Types text into whatever has focus on the device, as keystrokes: for passwords, links and notes. */
function TypeTextDialog({ open, onOpenChange, onType, wayland }: { open: boolean; onOpenChange: (open: boolean) => void; onType: (text: string) => void; wayland: boolean }) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!open) return;
    setText('');
    // Starts from the clipboard when the browser allows reading it.
    void navigator.clipboard?.readText?.().then(value => { if (value) setText(value.slice(0, MAX_TYPED)); }, () => undefined);
  }, [open]);
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 animate-fade" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,520px)] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-white/10 bg-[#13283a] p-5 text-[#d6e2ea] shadow-pop animate-pop">
          <Dialog.Title className="text-[15px] font-semibold text-white">Type text on the device</Dialog.Title>
          <Dialog.Description className="mt-1 text-[13px] text-[#8fa3b3]">It’s typed into whatever has focus there, one keystroke at a time. Line breaks press Enter.</Dialog.Description>
          {wayland && <p className="mt-2 text-[13px] text-[#8fa3b3]">GNOME can type only characters available in the computer’s active keyboard layout. Check the entered text before submitting it.</p>}
          <textarea value={text} onChange={event => setText(event.target.value.slice(0, MAX_TYPED))} rows={6} autoFocus
            className="mt-3 w-full resize-y rounded-lg border border-white/15 bg-[#0a1620] p-2.5 font-mono text-[13px] outline-none focus:border-[#F07522]" />
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="text-[12px] tabular text-[#8fa3b3]">{text.length} / {MAX_TYPED}</span>
            <div className="flex gap-2">
              <Dialog.Close className="inline-flex h-9 items-center rounded-lg border border-white/15 px-3.5 text-sm font-medium hover:bg-white/5">Cancel</Dialog.Close>
              <button type="button" disabled={!text} onClick={() => { onType(text); onOpenChange(false); }}
                className="inline-flex h-9 items-center rounded-lg bg-[#F07522] px-3.5 text-sm font-semibold text-white disabled:opacity-50">Type it</button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
